import { useId } from 'react';

import { Label, Radio, RadioGroup, Row, SelectOption, TextFieldProps } from '@scaleaq/scaleui';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import { responsive } from '@/utils/scaleUi';

type ControlledRadioFieldProps<T extends TextFieldProps> = {
  control: Control<T>;
  caution?: boolean | string;
  critical?: boolean | string;
  inputType?: 'text' | 'number';
  maxWidth?: string;
  name: Path<T>;
  render?: boolean;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  options: Array<SelectOption<string>>;
} & Omit<TextFieldProps, 'value' | 'onChange' | 'pattern' | 'type' | 'defaultValue'>;

export const ControlledRadioField = <T extends FieldValues>({
  control,
  caution,
  critical,

  label,
  options,

  name,
  render = true,
  rules,
  inputType = 'text',
  ...props
}: ControlledRadioFieldProps<T>) => {
  void inputType;
  const radioGroupName = useId();
  const hasValidationState = Boolean(caution || critical);

  if (!render) return;

  return (
    <div style={{ maxWidth: props.maxWidth, width: '100%' }}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <Label label={label}>
            <RadioGroup
              name={`${radioGroupName}-${String(name)}`}
              value={value}
              aria-invalid={hasValidationState}
              onChange={e => {
                onChange(e);
              }}
            >
              <Row style={{ flexWrap: 'wrap', gap: responsive.spacing.large }}>
                {options.map((o: SelectOption<string>) => (
                  <Radio
                    key={o.label}
                    {...o}
                    id={o.value}
                    color={hasValidationState ? 'critical' : 'action'}
                    style={{ flexGrow: '0' }}
                  />
                ))}
              </Row>
            </RadioGroup>
          </Label>
        )}
      />
    </div>
  );
};
